import MarketingNav from "@/components/MarketingNav";
import MarketingFooter from "@/components/MarketingFooter";

/**
 * Privacy Policy — long-form legal content.
 *
 * Renders inside the marketing-site shell (LegalLayout). All copy is
 * sourced from the canonical privacy policy text provided by Bloq legal;
 * any updates should land here verbatim. Heading levels follow the
 * marketing-site type scale: H2 for top-level sections, H3 for
 * sub-sections.
 */
function LegalLayout({ title, eyebrow, lastUpdated, children }) {
  return (
    <div className="bg-[#fbfcfc] min-h-screen">
      <MarketingNav />
      <section className="bloq-section">
        <div className="bloq-container max-w-3xl">
          <p className="bloq-eyebrow">{eyebrow}</p>
          <h1 className="bloq-display text-5xl mt-3 mb-3">{title}</h1>
          <p className="text-sm text-[#82828c] mb-12">Last updated: {lastUpdated}</p>
          <div className="prose prose-sm max-w-none text-[#4a4a52] leading-relaxed flex flex-col gap-5">
            {children}
          </div>
        </div>
      </section>
      <MarketingFooter />
    </div>
  );
}

const H2 = ({ children }) => (
  <h2 className="text-[#1a1a1f] font-semibold text-2xl mt-10 mb-1">{children}</h2>
);
const H3 = ({ children }) => (
  <h3 className="text-[#1a1a1f] font-semibold text-lg mt-6 mb-1">{children}</h3>
);
const UL = ({ children }) => (
  <ul className="list-disc pl-6 flex flex-col gap-2 my-1">{children}</ul>
);
const A = ({ href, children }) => (
  <a href={href} className="text-[#005e7a] underline hover:text-[#003c4f]">{children}</a>
);

export function Privacy() {
  return (
    <LegalLayout title="Privacy Policy" eyebrow="Privacy" lastUpdated="May 27, 2026">
      <p><strong>Effective Date:</strong> May 27, 2026</p>

      <p>
        Bloq, Inc. (&ldquo;Bloq,&rdquo; &ldquo;we,&rdquo; &ldquo;us,&rdquo; or &ldquo;our&rdquo;) respects your privacy. This Privacy Policy explains how we collect, use, disclose, store, and protect personal information when you use our websites, applications, video conferencing tools, booking features, meeting rooms, AI-powered summaries, transcripts, recordings, tasks, notes, emails, notifications, integrations, and related services.
      </p>
      <p>
        This Privacy Policy applies to Bloq websites, including <A href="https://bloq.video">bloq.video</A>, and to any Bloq products or services that link to this Privacy Policy.
      </p>
      <p>
        If you have questions or want to exercise privacy rights, contact us at <A href="mailto:privacy@bloq.video">privacy@bloq.video</A>.
      </p>

      <H2>1. Who We Are</H2>
      <p>
        Bloq is an AI-powered video conferencing, scheduling, booking, meeting productivity, and workflow platform. Bloq helps users host meetings, schedule appointments, create recordings and transcripts, generate AI summaries, manage tasks and follow-ups, send meeting-related emails, create Recall Reports, and organize meeting records.
      </p>
      <p>In this Privacy Policy:</p>
      <UL>
        <li><strong>&ldquo;Services&rdquo;</strong> means Bloq&rsquo;s websites, applications, software, meeting rooms, booking pages, communications, integrations, and related products.</li>
        <li><strong>&ldquo;Customer&rdquo;</strong> means the organization, workspace, company, team, or individual that creates or manages a Bloq account or workspace.</li>
        <li><strong>&ldquo;User&rdquo;</strong> means an individual who uses Bloq, including account holders, hosts, workspace members, invited meeting participants, guests, booking invitees, and website visitors.</li>
        <li><strong>&ldquo;Personal information&rdquo;</strong> or <strong>&ldquo;personal data&rdquo;</strong> means information that identifies, relates to, describes, can reasonably be associated with, or could reasonably be linked to an individual or household.</li>
      </UL>

      <H2>2. Information We Collect</H2>
      <p>
        We collect information in several ways, including information you provide, information generated through your use of Bloq, information collected automatically, and information received from third-party integrations or service providers.
      </p>

      <H3>2.1 Account and Profile Information</H3>
      <p>When you create an account, join a workspace, or use Bloq, we may collect:</p>
      <UL>
        <li>Name</li>
        <li>Email address</li>
        <li>Password or authentication credentials</li>
        <li>Profile photo, avatar, or initials</li>
        <li>Workspace name</li>
        <li>Company or organization name</li>
        <li>Role or permissions within a workspace</li>
        <li>Billing or subscription information</li>
        <li>Account preferences</li>
        <li>Communication preferences</li>
      </UL>

      <H3>2.2 Meeting Information</H3>
      <p>When you create, join, host, or participate in a meeting, we may collect:</p>
      <UL>
        <li>Meeting title</li>
        <li>Meeting description</li>
        <li>Meeting date, time, timezone, and duration</li>
        <li>Host information</li>
        <li>Participant names</li>
        <li>Participant email addresses</li>
        <li>Guest display names</li>
        <li>Join and leave times</li>
        <li>Attendance records</li>
        <li>Waiting room status</li>
        <li>Meeting settings, such as recording, transcription, AI summary, guest access, and waiting room preferences</li>
        <li>Chat messages</li>
        <li>Meeting notes</li>
        <li>Action items</li>
        <li>Decisions</li>
        <li>Tasks linked to meetings</li>
        <li>Follow-up notes</li>
        <li>Recap email drafts</li>
        <li>Meeting files and attachments</li>
        <li>Recall Reports and selected report sections</li>
      </UL>

      <H3>2.3 Audio, Video, Recording, and Screen Sharing Information</H3>
      <p>Depending on meeting settings and user actions, Bloq may process:</p>
      <UL>
        <li>Audio streams</li>
        <li>Video streams</li>
        <li>Screen sharing content</li>
        <li>Meeting recordings</li>
        <li>Recording metadata</li>
        <li>Microphone and camera status</li>
        <li>Device settings, such as selected microphone or camera</li>
        <li>Speaker or audio activity indicators</li>
        <li>Participant media status, such as muted/unmuted or camera on/off</li>
      </UL>
      <p>
        Bloq may process this information to provide live meeting functionality, recording, transcription, screen sharing, meeting records, and related features.
      </p>
      <p>
        Live video, audio, cloud recording, and speech-to-text functionality is delivered through Agora, a third-party real-time communications provider. Agora processes meeting audio and video streams as a service provider to Bloq under appropriate contractual confidentiality and security obligations.
      </p>

      <H3>2.4 Transcripts, AI Summaries, and AI-Generated Content</H3>
      <p>If transcription or AI features are enabled, Bloq may collect, process, and store:</p>
      <UL>
        <li>Live transcript text</li>
        <li>Final transcript text</li>
        <li>Transcript timestamps</li>
        <li>Speaker labels where available</li>
        <li>Meeting chat content used for summaries</li>
        <li>Meeting notes used for summaries</li>
        <li>Action items</li>
        <li>Decisions</li>
        <li>AI-generated summaries</li>
        <li>AI-generated follow-up email drafts</li>
        <li>AI-generated suggested tasks or action items</li>
        <li>AI processing status and error logs</li>
      </UL>
      <p>
        Bloq&rsquo;s browser-based transcription may only capture the microphone of the user who enabled transcription, unless full-room transcription or server-side transcription is configured. AI-generated content may be inaccurate or incomplete. Users should review AI summaries, action items, decisions, and email drafts before relying on, sending, or sharing them.
      </p>

      <H3>2.5 Booking and Scheduling Information</H3>
      <p>When users create or book meetings through Bloq, we may collect:</p>
      <UL>
        <li>Booking link information</li>
        <li>Appointment type</li>
        <li>Availability settings</li>
        <li>Invitee name</li>
        <li>Invitee email</li>
        <li>Invitee phone number, if provided</li>
        <li>Selected date, time, timezone, and duration</li>
        <li>Booking form responses</li>
        <li>Custom intake form responses</li>
        <li>Cancellation or rescheduling information</li>
        <li>Payment status, if paid bookings are enabled</li>
        <li>Calendar-related metadata, if calendar integrations are connected</li>
      </UL>

      <H3>2.6 Tasks, Notes, Contacts, and Follow-Ups</H3>
      <p>Bloq may collect and store productivity-related information, including:</p>
      <UL>
        <li>Tasks</li>
        <li>Task assignments</li>
        <li>Task status, priority, due dates, comments, and subtasks</li>
        <li>Meeting-linked notes</li>
        <li>Contact information</li>
        <li>Follow-up notes</li>
        <li>Follow-up email drafts</li>
        <li>Decisions</li>
        <li>Action items</li>
        <li>User-generated content related to meetings and workflows</li>
      </UL>

      <H3>2.7 Communications and Email Notifications</H3>
      <p>Bloq may collect and process information related to emails and notifications, including:</p>
      <UL>
        <li>Recipient email addresses</li>
        <li>Email subject lines</li>
        <li>Email body content</li>
        <li>Email templates</li>
        <li>Email delivery status</li>
        <li>Scheduled send details</li>
        <li>Notification preferences</li>
        <li>Email logs</li>
        <li>Provider delivery responses</li>
        <li>Attachments, including Recall Reports</li>
      </UL>
      <p>
        If a workspace enables inbound email routing, such as routing public contact-form submissions or reply-to-thread messages into Bloq, those inbound emails are received and parsed by a third-party email provider (currently Postmark) on Bloq&rsquo;s behalf and then stored within the receiving workspace. Sender names, sender email addresses, message subjects, and message bodies may be retained as part of the workspace&rsquo;s contact, lead, or conversation records.
      </p>

      <H3>2.8 Payment and Billing Information</H3>
      <p>If paid features are enabled, we may collect billing-related information, such as:</p>
      <UL>
        <li>Subscription plan</li>
        <li>Billing name and email</li>
        <li>Payment status</li>
        <li>Invoice information</li>
        <li>Transaction identifiers</li>
        <li>Subscription status</li>
        <li>Payment provider metadata</li>
      </UL>
      <p>
        Bloq does not intentionally store full payment card numbers. Payment processing may be handled by third-party payment processors, such as Stripe or another provider.
      </p>
      <p>
        For appointment-type and booking-link payments made by an invitee to a workspace owner, Bloq uses Stripe Connect Express. In that flow, the workspace owner is the merchant of record for the transaction and the funds are paid directly into the workspace owner&rsquo;s own connected Stripe account, not to Bloq. Bloq receives transaction metadata (such as session identifiers, payment status, amount, currency, and a reference to the connected Stripe account) for the purpose of marking the booking paid and powering payment-related features inside the workspace, but does not receive or store the invitee&rsquo;s payment-card details.
      </p>

      <H3>2.9 Integrations and Third-Party Services</H3>
      <p>
        If you connect third-party services to Bloq, we may collect information necessary to provide those integrations, such as:
      </p>
      <UL>
        <li>Calendar availability</li>
        <li>Calendar event metadata</li>
        <li>Connected account email</li>
        <li>Integration status</li>
        <li>Authentication tokens or authorization references</li>
        <li>Provider identifiers</li>
        <li>Webhook delivery information</li>
      </UL>
      <p>Where possible, Bloq uses only the data needed to provide the requested integration.</p>

      <H3>2.10 Device, Usage, and Technical Information</H3>
      <p>We may automatically collect:</p>
      <UL>
        <li>IP address</li>
        <li>Device type</li>
        <li>Browser type</li>
        <li>Operating system</li>
        <li>Referring URLs</li>
        <li>Pages viewed</li>
        <li>Clicks and interactions</li>
        <li>Session information</li>
        <li>Log data</li>
        <li>Error reports</li>
        <li>Performance data</li>
        <li>Approximate location derived from IP address</li>
        <li>Cookies and similar technologies</li>
        <li>Authentication and security logs</li>
      </UL>

      <H3>2.11 Sensitive Personal Information</H3>
      <p>
        Some information processed through Bloq may be considered sensitive under certain privacy laws, depending on how users use the platform. This may include audio, video, meeting recordings, transcripts, login credentials, private communications, screen sharing, and sensitive content shared by users during meetings, chats, notes, transcripts, or uploaded files.
      </p>
      <p>
        Bloq does not intentionally require users to submit sensitive personal information unless it is necessary to use a requested feature or the user chooses to provide it.
      </p>

      <H3>2.12 Google User Data Specifically</H3>
      <p>
        When you choose to connect your Google account to Bloq, Bloq may receive specific Google user data through Google APIs in addition to the categories described above. This data is described in detail in Section 7 (&ldquo;Google User Data and Google API Services&rdquo;), and includes:
      </p>
      <UL>
        <li>Your Google account email address and basic profile information used to identify the connected Google account.</li>
        <li>Your Google Calendar list and calendar metadata, such as calendar name, time zone, color, access role, and primary indicator.</li>
        <li>Calendar events on the calendars you choose to connect, including event title, start and end times, location, description, attendees, recurrence, and conference data.</li>
        <li>Free or busy availability information from calendars you designate for availability blocking.</li>
      </UL>
      <p>
        Google user data is collected only after you grant the relevant OAuth permission and only for the purposes described in Section 7. You can disconnect your Google account at any time from your Bloq integration settings, and you can revoke Bloq&rsquo;s access directly from your Google Account permissions page.
      </p>

      <H2>3. How We Use Information</H2>
      <p>Bloq may use personal information to:</p>
      <UL>
        <li>Provide, operate, maintain, and improve the Services</li>
        <li>Create and manage accounts and workspaces</li>
        <li>Host video meetings</li>
        <li>Enable screen sharing, chat, transcription, and recording</li>
        <li>Generate and store AI summaries, action items, decisions, and follow-up drafts</li>
        <li>Create and manage Recall Reports</li>
        <li>Schedule meetings and manage bookings</li>
        <li>Send meeting invitations, reminders, confirmations, updates, and follow-ups</li>
        <li>Provide tasks, notes, contacts, and productivity features</li>
        <li>Process billing and subscriptions</li>
        <li>Provide customer support</li>
        <li>Improve product performance and reliability</li>
        <li>Debug errors and maintain security</li>
        <li>Detect, prevent, and respond to fraud, abuse, security incidents, or unlawful activity</li>
        <li>Personalize user experience</li>
        <li>Manage integrations</li>
        <li>Comply with legal obligations</li>
        <li>Enforce our agreements, terms, and policies</li>
        <li>Send administrative, transactional, and service-related communications</li>
        <li>Send marketing communications where permitted by law and user preferences</li>
      </UL>

      <H2>4. Legal Bases for Processing Under GDPR</H2>
      <p>
        If you are located in the European Economic Area, United Kingdom, or Switzerland, we process personal data under one or more lawful bases, including:
      </p>
      <UL>
        <li><strong>Contract:</strong> to provide Bloq services you or your organization requested.</li>
        <li><strong>Legitimate interests:</strong> to operate, secure, improve, and support Bloq, provided those interests are not overridden by your rights.</li>
        <li><strong>Consent:</strong> where required, such as for certain cookies, marketing communications, optional AI features, or other consent-based processing.</li>
        <li><strong>Legal obligation:</strong> to comply with applicable laws, regulations, legal requests, or compliance obligations.</li>
        <li><strong>Vital interests or public interest:</strong> where applicable in limited circumstances.</li>
      </UL>

      <H2>5. Customer Content and Meeting Content</H2>
      <p>
        Bloq allows users to create, upload, generate, and store content, including meeting recordings, chat messages, notes, transcripts, summaries, action items, decisions, follow-up drafts, files, and reports.
      </p>
      <p>
        This content may include personal information, confidential business information, or sensitive information. Customers and users are responsible for ensuring they have the right to collect, share, record, transcribe, upload, or process content through Bloq.
      </p>
      <p>
        If you use Bloq on behalf of an organization, that organization may control certain information in your workspace, including meeting records, tasks, transcripts, summaries, recordings, and reports.
      </p>

      <H2>6. AI Features</H2>
      <p>Bloq may provide AI-powered features, including:</p>
      <UL>
        <li>Meeting summaries</li>
        <li>Transcript analysis</li>
        <li>Suggested action items</li>
        <li>Suggested decisions</li>
        <li>Follow-up email drafts</li>
        <li>Recall Report support</li>
        <li>Search and productivity assistance</li>
      </UL>
      <p>AI features may process meeting content, transcripts, notes, chat, task data, and related meeting records to generate outputs.</p>
      <p>
        AI-generated content may be inaccurate, incomplete, or inappropriate for your specific use. Users should review and edit AI outputs before relying on them, sending them, or sharing them.
      </p>
      <p>
        Where AI features use third-party AI providers, Bloq may send necessary content to those providers to generate the requested output. Bloq will take reasonable steps to configure AI providers in a privacy-conscious manner and limit processing to the purpose of providing the requested feature.
      </p>

      <H2>7. Google User Data and Google API Services</H2>
      <p>
        This section explains how Bloq accesses, uses, stores, shares, and deletes data we receive through Google APIs when you connect your Google account to Bloq. This section supplements the rest of this Privacy Policy and controls in case of any conflict regarding Google user data specifically.
      </p>

      <H3>7.1 Google User Data We Receive</H3>
      <p>When you authorize Bloq to access your Google account, Bloq may request and receive the following Google user data through Google APIs:</p>
      <UL>
        <li>Your Google account email address and basic profile information, used to identify your connected Google account inside Bloq.</li>
        <li>Your Google Calendar list and calendar metadata, including calendar name, time zone, color, access role, and primary indicator.</li>
        <li>Calendar events on the calendars you choose to connect, including event title, start and end times, time zone, location, description, attendees, recurrence, conference data, and event identifiers.</li>
        <li>Free or busy availability information from calendars you designate as availability-blocking.</li>
      </UL>
      <p>Bloq requests only the OAuth scopes necessary to provide the features you have enabled.</p>

      <H3>7.2 How Bloq Uses Google User Data</H3>
      <p>Bloq uses Google user data solely to provide the user-facing features you have requested, specifically:</p>
      <UL>
        <li><strong>Calendar availability checks</strong> &mdash; to detect conflicts and prevent double-booking when invitees book time with you through a Bloq booking link.</li>
        <li><strong>Two-way calendar sync</strong> &mdash; to write Bloq-created bookings and meetings to your Google Calendar as events, and to update, reschedule, or cancel those events when corresponding bookings change in Bloq.</li>
        <li><strong>Inbound calendar awareness</strong> &mdash; to recognize when Bloq-created events are modified or canceled in Google Calendar and reflect those changes inside Bloq.</li>
        <li><strong>Calendar selection</strong> &mdash; to let you choose which Google calendars block your Bloq availability and which calendar Bloq writes events into.</li>
        <li><strong>Account identification</strong> &mdash; to display the connected Google account email in your Bloq Integration settings so you can confirm which Google account is linked.</li>
      </UL>
      <p>Bloq does NOT:</p>
      <UL>
        <li>Use Google user data to develop, train, or improve generalized or non-personalized artificial intelligence or machine learning models.</li>
        <li>Use Google user data for advertising, including retargeting, personalized advertising, or interest-based advertising.</li>
        <li>Sell Google user data, license Google user data, or rent Google user data to any third party.</li>
        <li>Read, process, or analyze Google user data for any purpose other than providing the user-facing features described above.</li>
        <li>Allow Bloq personnel to read Google user data except (i) where you have given explicit consent, (ii) where reading the data is strictly necessary for security investigations or to comply with applicable law, or (iii) where the data has been aggregated and anonymized for internal operations such as troubleshooting and abuse prevention.</li>
      </UL>

      <H3>7.3 How Bloq Stores Google User Data</H3>
      <p>Bloq stores only the minimum Google user data required to provide the requested features:</p>
      <UL>
        <li><strong>OAuth tokens</strong> are stored on Bloq servers in encrypted form and used only to call Google APIs on your behalf.</li>
        <li><strong>Calendar list metadata</strong> is cached so the integration settings page can display your calendars without re-querying Google on every visit.</li>
        <li><strong>Sync state tokens</strong> are used to fetch only events that have changed since the last sync, minimizing the volume of Google data exchanged.</li>
        <li><strong>Event identifiers</strong> are stored to link Bloq bookings and meetings to their corresponding Google Calendar events for two-way sync.</li>
      </UL>
      <p>
        Bloq does not retain the full content of Google Calendar events outside the duration of an active sync operation, except for the limited set of metadata fields required to render the event inside Bloq&rsquo;s user-facing calendar features.
      </p>

      <H3>7.4 How Bloq Shares Google User Data</H3>
      <p>Bloq does not sell Google user data. Bloq does not transfer Google user data to any third party except:</p>
      <UL>
        <li>To Google itself, through official Google APIs, in order to provide the features you have requested.</li>
        <li>To Bloq&rsquo;s hosting, database, and infrastructure service providers, as necessary to operate the Bloq platform, and only under appropriate contractual confidentiality, security, and data-processing obligations.</li>
        <li>Where disclosure is required by law, regulation, subpoena, court order, or other valid legal process, or where Bloq believes in good faith that disclosure is necessary to protect the rights, property, or safety of Bloq, its users, or others.</li>
      </UL>
      <p>Bloq does not transfer Google user data to any third-party artificial intelligence provider for the purpose of training generalized AI models.</p>

      <H3>7.5 Data Retention and Deletion of Google User Data</H3>
      <p>
        Google user data is retained only as long as needed to provide the Google integration, plus a short period for security, reliability, and audit purposes.
      </p>
      <p>You may at any time:</p>
      <UL>
        <li><strong>Disconnect your Google account</strong> from Bloq&rsquo;s Integration Settings page. Disconnection revokes Bloq&rsquo;s stored OAuth tokens and stops all future Google API access on your behalf.</li>
        <li><strong>Revoke Bloq&rsquo;s access directly from your Google Account</strong> at <A href="https://myaccount.google.com/permissions">https://myaccount.google.com/permissions</A>.</li>
        <li><strong>Request deletion</strong> of any remaining Google user data Bloq has stored by emailing <A href="mailto:privacy@bloq.video">privacy@bloq.video</A>.</li>
      </UL>
      <p>
        Upon disconnection, revocation, or a verified deletion request, Bloq will delete the stored OAuth tokens and any cached Google user data within a reasonable period, except where limited retention is required by applicable law or for legitimate security and audit purposes.
      </p>

      <H3>7.6 Compliance with Google API Services User Data Policy</H3>
      <p>
        <strong>Bloq&rsquo;s use and transfer to any other app of information received from Google APIs will adhere to the <A href="https://developers.google.com/terms/api-services-user-data-policy">Google API Services User Data Policy</A>, including the Limited Use requirements.</strong>
      </p>
      <p>
        If you believe Bloq is not in compliance with the Google API Services User Data Policy, please contact <A href="mailto:privacy@bloq.video">privacy@bloq.video</A>.
      </p>

      <H3>7.7 Microsoft Outlook and Other Third-Party Calendar Providers</H3>
      <p>
        Bloq also supports a Microsoft Outlook Calendar integration (via Microsoft Graph). When you choose to connect a Microsoft account, the same principles described in this section apply on a parallel basis: Bloq receives only the calendar data necessary to provide availability checks and two-way calendar sync, stores OAuth tokens in encrypted form, does not use the data for advertising or AI model training, does not sell or rent the data, and lets you disconnect at any time from your Bloq integration settings or directly from your Microsoft account. References to &ldquo;Google user data&rdquo; in Sections 7.2 through 7.5 should be read to apply equivalently to Microsoft Outlook calendar data where Bloq is acting under a Microsoft Graph authorization.
      </p>

      <H2>8. Recording, Transcription, and Consent</H2>
      <p>Bloq may allow users to record meetings, transcribe conversations, and generate summaries.</p>
      <p>
        Meeting hosts and users are responsible for complying with applicable recording, wiretapping, consent, employment, privacy, and communications laws. Some jurisdictions require all participants to consent before recording or transcribing a conversation.
      </p>
      <p>
        Bloq may display indicators or notices when recording, transcription, or AI features are enabled, but users remain responsible for providing legally required notices and obtaining legally required consents.
      </p>

      <H2>9. How We Share Information</H2>

      <H3>9.1 Service Providers</H3>
      <p>We may share information with vendors and service providers that help us operate Bloq, such as:</p>
      <UL>
        <li>Cloud hosting providers</li>
        <li>Video infrastructure providers</li>
        <li>Email providers</li>
        <li>AI service providers</li>
        <li>Analytics providers</li>
        <li>Payment processors</li>
        <li>Customer support tools</li>
        <li>Security and monitoring providers</li>
        <li>File storage providers</li>
        <li>Calendar or integration providers</li>
      </UL>
      <p>These providers may process information only as necessary to provide services to Bloq and subject to appropriate contractual obligations.</p>

      <H3>9.2 Workspace Administrators and Other Users</H3>
      <p>
        If you use Bloq as part of a workspace or organization, your information may be visible to workspace owners, administrators, hosts, co-hosts, or other authorized users, depending on settings and permissions.
      </p>
      <p>
        Meeting information may be visible to participants, hosts, invited users, or workspace members, depending on the feature and access permissions.
      </p>

      <H3>9.3 Meeting Participants and Invitees</H3>
      <p>Depending on meeting settings, Bloq may share:</p>
      <UL>
        <li>Your display name</li>
        <li>Chat messages</li>
        <li>Audio/video presence</li>
        <li>Mic/camera status</li>
        <li>Meeting participation status</li>
        <li>Notes, tasks, action items, decisions, summaries, transcripts, recordings, or reports where shared by the host or workspace</li>
      </UL>

      <H3>9.4 Integrated Services</H3>
      <p>
        If you connect Bloq with third-party services, such as calendars, email providers, payment processors, or productivity tools, information may be shared with those services as necessary to provide the integration.
      </p>

      <H3>9.5 Legal, Safety, and Compliance Reasons</H3>
      <p>We may disclose information if we believe it is necessary to:</p>
      <UL>
        <li>Comply with law, regulation, subpoena, court order, or legal process</li>
        <li>Respond to lawful government requests</li>
        <li>Protect the rights, property, or safety of Bloq, users, or others</li>
        <li>Detect, prevent, or investigate fraud, abuse, or security incidents</li>
        <li>Enforce our agreements or policies</li>
        <li>Defend legal claims</li>
      </UL>

      <H3>9.6 Business Transfers</H3>
      <p>
        If Bloq is involved in a merger, acquisition, financing, reorganization, bankruptcy, sale of assets, or similar transaction, personal information may be transferred as part of that transaction, subject to applicable law.
      </p>

      <H2>10. Selling, Sharing, and Targeted Advertising</H2>
      <p>Bloq does not intend to sell personal information in the traditional sense.</p>
      <p>
        Some privacy laws define &ldquo;sale,&rdquo; &ldquo;sharing,&rdquo; or &ldquo;targeted advertising&rdquo; broadly to include certain advertising, analytics, or cross-context behavioral advertising activities. If Bloq engages in activities considered &ldquo;selling,&rdquo; &ldquo;sharing,&rdquo; or &ldquo;targeted advertising&rdquo; under applicable law, we will provide required disclosures and opt-out mechanisms.
      </p>
      <p>
        You may contact us at <A href="mailto:privacy@bloq.video">privacy@bloq.video</A> to exercise applicable opt-out rights.
      </p>

      <H2>11. Cookies and Similar Technologies</H2>
      <p>Bloq may use cookies, pixels, local storage, SDKs, and similar technologies to:</p>
      <UL>
        <li>Keep users signed in</li>
        <li>Remember preferences</li>
        <li>Secure accounts</li>
        <li>Measure performance</li>
        <li>Understand product usage</li>
        <li>Improve the Services</li>
        <li>Support analytics</li>
        <li>Support marketing where permitted</li>
      </UL>
      <p>
        Users may control cookies through browser settings. Some features may not work properly if cookies are disabled. Where required by law, Bloq will request consent for certain cookies or tracking technologies.
      </p>

      <H2>12. Data Retention</H2>
      <p>
        We retain personal information for as long as necessary to provide the Services, comply with legal obligations, resolve disputes, enforce agreements, maintain security, and support legitimate business purposes.
      </p>
      <p>Retention periods may vary depending on:</p>
      <UL>
        <li>Account status</li>
        <li>Workspace settings</li>
        <li>Subscription plan</li>
        <li>Type of data</li>
        <li>Legal requirements</li>
        <li>User or customer deletion requests</li>
        <li>Backup and security needs</li>
      </UL>
      <p>
        Meeting records, recordings, transcripts, summaries, notes, tasks, and Recall Reports may remain available to workspace owners or authorized users unless deleted according to workspace settings, product functionality, or applicable law.
      </p>

      <H2>13. Data Security</H2>
      <p>Bloq uses reasonable administrative, technical, and organizational safeguards designed to protect personal information.</p>
      <p>These safeguards may include:</p>
      <UL>
        <li>Access controls</li>
        <li>Authentication</li>
        <li>Encryption in transit</li>
        <li>Secure storage practices</li>
        <li>Logging and monitoring</li>
        <li>Role-based permissions</li>
        <li>Vendor security reviews</li>
        <li>Incident response processes</li>
      </UL>
      <p>No system can be guaranteed 100% secure. Users are responsible for protecting their account credentials and using Bloq appropriately.</p>

      <H2>14. International Data Transfers</H2>
      <p>Bloq may process and store information in the United States and other countries where Bloq or its service providers operate.</p>
      <p>
        If personal data is transferred from the European Economic Area, United Kingdom, Switzerland, or another jurisdiction with data transfer rules, Bloq will use appropriate transfer mechanisms where required, such as Standard Contractual Clauses or other legally recognized safeguards.
      </p>

      <H2>15. Your Privacy Rights</H2>
      <p>Depending on where you live, you may have certain privacy rights. These may include the right to:</p>
      <UL>
        <li>Access personal information</li>
        <li>Correct inaccurate personal information</li>
        <li>Delete personal information</li>
        <li>Obtain a copy of personal information</li>
        <li>Port personal information</li>
        <li>Restrict or object to processing</li>
        <li>Opt out of sale, sharing, targeted advertising, or certain profiling</li>
        <li>Limit the use or disclosure of sensitive personal information</li>
        <li>Withdraw consent</li>
        <li>Appeal a privacy request decision</li>
        <li>Lodge a complaint with a data protection authority</li>
      </UL>
      <p>To exercise your rights, contact us at <A href="mailto:privacy@bloq.video">privacy@bloq.video</A>.</p>
      <p>
        We may need to verify your identity before fulfilling a request. If you are part of a workspace controlled by an organization, we may direct your request to that organization or process it according to our role as a service provider or processor.
      </p>

      <H2>16. U.S. State Privacy Rights</H2>
      <p>
        Several U.S. states have enacted comprehensive privacy laws, and the U.S. state privacy landscape continues to expand. Depending on your state, you may have rights related to:
      </p>
      <UL>
        <li>Accessing personal information</li>
        <li>Deleting personal information</li>
        <li>Correcting inaccurate information</li>
        <li>Obtaining a portable copy of information</li>
        <li>Opting out of targeted advertising</li>
        <li>Opting out of the sale of personal information</li>
        <li>Opting out of certain profiling</li>
        <li>Appealing a denied privacy request</li>
      </UL>
      <p>Bloq will respond to applicable state privacy requests as required by law. To submit a request, contact us at <A href="mailto:privacy@bloq.video">privacy@bloq.video</A>.</p>

      <H2>17. California Privacy Notice</H2>
      <p>This section applies to California residents and supplements the rest of this Privacy Policy.</p>
      <p>
        California residents have rights under the California Consumer Privacy Act, as amended by the California Privacy Rights Act, commonly referred to as the CCPA/CPRA.
      </p>

      <H3>17.1 Categories of Personal Information We May Collect</H3>
      <p>In the last 12 months, Bloq may have collected the following categories of personal information:</p>
      <UL>
        <li><strong>Identifiers:</strong> such as name, email address, IP address, account ID, or device identifiers.</li>
        <li><strong>Customer records information:</strong> such as billing or account information.</li>
        <li><strong>Commercial information:</strong> such as subscription plan, payment status, or transaction metadata.</li>
        <li><strong>Internet or electronic network activity:</strong> such as usage data, logs, device data, and interaction data.</li>
        <li><strong>Geolocation information:</strong> such as approximate location derived from IP address.</li>
        <li><strong>Audio, electronic, visual, or similar information:</strong> such as meeting audio, video, recordings, transcripts, and screen sharing content.</li>
        <li><strong>Professional or employment-related information:</strong> such as company, role, workspace, or business contact information.</li>
        <li><strong>Inferences:</strong> such as product preferences or usage patterns.</li>
        <li><strong>Sensitive personal information:</strong> such as account login credentials, meeting content, recordings, transcripts, or private communications where applicable.</li>
      </UL>

      <H3>17.2 Sources of Personal Information</H3>
      <p>We may collect personal information from:</p>
      <UL>
        <li>You</li>
        <li>Your workspace or organization</li>
        <li>Meeting hosts and participants</li>
        <li>Booking invitees</li>
        <li>Connected integrations</li>
        <li>Service providers</li>
        <li>Cookies and similar technologies</li>
        <li>Device and usage logs</li>
        <li>Payment processors</li>
        <li>Email and notification providers</li>
      </UL>

      <H3>17.3 Business or Commercial Purposes</H3>
      <p>We may use personal information for:</p>
      <UL>
        <li>Providing Bloq services</li>
        <li>Hosting meetings</li>
        <li>Processing recordings, transcripts, summaries, notes, tasks, and reports</li>
        <li>Managing bookings and scheduling</li>
        <li>Sending notifications and emails</li>
        <li>Maintaining accounts and workspaces</li>
        <li>Billing and subscriptions</li>
        <li>Security and fraud prevention</li>
        <li>Analytics and product improvement</li>
        <li>Customer support</li>
        <li>Legal compliance</li>
      </UL>

      <H3>17.4 Categories of Personal Information Disclosed</H3>
      <p>We may disclose the categories of personal information listed above to:</p>
      <UL>
        <li>Service providers</li>
        <li>Contractors</li>
        <li>Payment processors</li>
        <li>Cloud infrastructure providers</li>
        <li>AI providers</li>
        <li>Email providers</li>
        <li>Video infrastructure providers</li>
        <li>Analytics providers</li>
        <li>Workspace administrators</li>
        <li>Meeting participants, depending on settings</li>
        <li>Legal or regulatory authorities where required</li>
      </UL>

      <H3>17.5 Sale or Sharing</H3>
      <p>
        Bloq does not intend to sell personal information in the traditional sense. If Bloq engages in activities that constitute &ldquo;sale&rdquo; or &ldquo;sharing&rdquo; under California law, California residents may opt out.
      </p>
      <p>You may contact us at <A href="mailto:privacy@bloq.video">privacy@bloq.video</A> to exercise applicable opt-out rights.</p>

      <H3>17.6 Sensitive Personal Information</H3>
      <p>
        Bloq may process sensitive personal information where necessary to provide requested services, such as account security, meeting recordings, transcripts, private communications, and AI-powered meeting features.
      </p>
      <p>
        Bloq does not use sensitive personal information for purposes that would require a right to limit under California law unless we provide the required notice and ability to limit.
      </p>

      <H3>17.7 California Privacy Rights</H3>
      <p>California residents may request to:</p>
      <UL>
        <li>Know what personal information we collect, use, disclose, sell, or share</li>
        <li>Access personal information</li>
        <li>Delete personal information</li>
        <li>Correct inaccurate personal information</li>
        <li>Opt out of sale or sharing</li>
        <li>Limit certain uses of sensitive personal information</li>
        <li>Not be discriminated against for exercising privacy rights</li>
      </UL>
      <p>To exercise these rights, contact us at <A href="mailto:privacy@bloq.video">privacy@bloq.video</A>.</p>

      <H3>17.8 Authorized Agents</H3>
      <p>
        California residents may use an authorized agent to submit privacy requests. We may require proof of authorization and may ask the consumer to verify their identity directly.
      </p>

      <H3>17.9 Non-Discrimination</H3>
      <p>
        We will not discriminate against you for exercising privacy rights. We will not deny services, charge different prices, or provide a different quality of service because you exercised applicable privacy rights, except as permitted by law.
      </p>

      <H2>18. GDPR, UK GDPR, and European Privacy Rights</H2>
      <p>If you are located in the European Economic Area, United Kingdom, or Switzerland, you may have the right to:</p>
      <UL>
        <li>Access personal data</li>
        <li>Correct inaccurate personal data</li>
        <li>Delete personal data</li>
        <li>Restrict processing</li>
        <li>Object to processing</li>
        <li>Data portability</li>
        <li>Withdraw consent</li>
        <li>Lodge a complaint with a supervisory authority</li>
      </UL>
      <p>
        Where Bloq acts as a processor on behalf of a Customer, the Customer may be the controller of your personal data. In that case, you may need to contact the Customer directly to exercise certain rights.
      </p>
      <p>To contact Bloq about European privacy rights, email <A href="mailto:privacy@bloq.video">privacy@bloq.video</A>.</p>

      <H2>19. Children&rsquo;s Privacy</H2>
      <p>Bloq is not intended for children under 13, and we do not knowingly collect personal information from children under 13.</p>
      <p>
        If you believe a child has provided personal information to Bloq, contact us at <A href="mailto:privacy@bloq.video">privacy@bloq.video</A> so we can take appropriate action.
      </p>
      <p>If a higher age threshold applies in your jurisdiction, Bloq will comply with applicable law.</p>

      <H2>20. Marketing Communications</H2>
      <p>If you receive marketing emails from Bloq, you may opt out using the unsubscribe link in the email or by contacting us.</p>
      <p>
        Even if you opt out of marketing communications, we may still send transactional or service-related messages, such as account, security, billing, meeting, booking, or legal notices.
      </p>

      <H2>21. Do Not Track and Global Privacy Controls</H2>
      <p>Some browsers offer &ldquo;Do Not Track&rdquo; or similar signals. There is no uniform standard for how websites should respond to all such signals.</p>
      <p>
        Where required by applicable law, Bloq will honor legally recognized opt-out preference signals, such as Global Privacy Control signals, for applicable opt-out rights.
      </p>

      <H2>22. Third-Party Links and Services</H2>
      <p>Bloq may contain links to third-party websites, services, integrations, or applications. This Privacy Policy does not apply to third-party services.</p>
      <p>We encourage you to review the privacy policies of third-party services before using them.</p>
      <p>
        Some pages on the Bloq marketing site may also embed third-party forms or widgets (for example, embedded Cognito Forms used for sign-ups, beta access, or contact requests). When you submit data through an embedded third-party form, that data is transmitted to and processed by the third-party form provider under that provider&rsquo;s own privacy policy, separately from Bloq&rsquo;s direct processing.
      </p>

      <H2>23. Data Processing Agreements</H2>
      <p>If you use Bloq on behalf of an organization and need a data processing agreement, contact <A href="mailto:privacy@bloq.video">privacy@bloq.video</A>.</p>
      <p>
        Where required, Bloq may enter into appropriate data protection terms with Customers, including terms addressing processing instructions, confidentiality, security, subprocessors, assistance with rights requests, and international transfers.
      </p>

      <H2>24. Changes to This Privacy Policy</H2>
      <p>We may update this Privacy Policy from time to time.</p>
      <p>
        If we make material changes, we may notify users by email, in-app notice, website notice, or other appropriate method. The updated Privacy Policy will indicate the &ldquo;Last Updated&rdquo; date.
      </p>
      <p>Your continued use of Bloq after an updated Privacy Policy becomes effective means you acknowledge the updated policy.</p>

      <H2>25. Contact Us</H2>
      <p>For questions, requests, or concerns about this Privacy Policy or Bloq&rsquo;s privacy practices, contact:</p>
      <p>
        <strong>Bloq Privacy Team</strong><br />
        Email: <A href="mailto:privacy@bloq.video">privacy@bloq.video</A>
      </p>
    </LegalLayout>
  );
}
export default Privacy;
